import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import deleteUserApi from '@/api/delete/user/delete-user';
import getUserApi from '@/api/get/user-management/get-user';
import Alert from '@/components/alert/alert-component';
import Card from '@/components/card/card-component';
import SubmitCard from '@/components/card/submit-card-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import { Col, Row } from '@/components/grid/grid-component';
import Loader from '@/components/loader/loader-component';
import Pill from '@/components/pill/pill-component';
import StatusIndicator from '@/components/status-indicator/status-indicator-component';
import { useMutation, useQuery } from '@/hooks/use-api';
import { BodyText, Heading3Text, SmallTitle } from '@/utils/global-fonts';
import { toSentenceCase } from '@/utils/utils';
import Styled from './user-view-style';

const mainStyledCol = {
  maxWidth: '93%',
  width: '100%',
};

/**
 * User view component
 * @returns {JSX.Element}
 */
const UserView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: userData, loading } = useQuery(getUserApi, id);

  const [deleteUser, { loading: deleteUserLoading }] =
    useMutation(deleteUserApi);

  const launchDeleteConfirmation = useCallback(() => {
    ConfirmationModal({
      title: 'Delete User',
      text: `Are you sure you want to delete user?`,
      onConfirm: () =>
        deleteUser(id).then(() => {
          navigate('/app/user-management');
          Alert({
            icon: 'success',
            text: 'User deleted successfully',
          });
        }),
    });
  }, [deleteUser, id, navigate]);

  if (!deleteUserLoading && !loading) {
    return (
      <Row>
        <Card padding="12px 16px">
          <Col style={mainStyledCol} xs={12}>
            <Styled.StyledRow
              direction="row"
              align="center"
              justify="space-between"
            >
              <Styled.Span>
                <Heading3Text className="title">View User</Heading3Text>
                <Pill type={toSentenceCase(userData?.user_type)} />
              </Styled.Span>
              <StatusIndicator status={userData?.status} />
            </Styled.StyledRow>
            <Row>
              <Col xs={6}>
                <Styled.DivStyle>
                  <SmallTitle>FIRST NAME</SmallTitle>
                  <BodyText>{toSentenceCase(userData?.first_name)}</BodyText>
                </Styled.DivStyle>
              </Col>
              <Col xs={6}>
                <Styled.DivStyle>
                  <SmallTitle>LAST NAME</SmallTitle>
                  <BodyText>{toSentenceCase(userData?.last_name)}</BodyText>
                </Styled.DivStyle>
              </Col>
              <Col xs={6}>
                <Styled.DivStyle>
                  <SmallTitle>EMAIL ADDRESS</SmallTitle>
                  <BodyText>{userData?.email}</BodyText>
                </Styled.DivStyle>
              </Col>
              <Col xs={6}>
                <Styled.DivStyle>
                  <SmallTitle>CONTACT NUMBER</SmallTitle>
                  <Styled.PhoneNumberContainer>
                    <Styled.PhoneInput
                      name="contact_number"
                      disabled
                      value={userData?.contact_number}
                    />
                    <BodyText>{userData?.contact_number}</BodyText>
                  </Styled.PhoneNumberContainer>
                </Styled.DivStyle>
              </Col>
              <Col xs={6}>
                <Styled.DivStyle>
                  <SmallTitle>USER ROLE</SmallTitle>
                  <BodyText>{userData?.role_name}</BodyText>
                </Styled.DivStyle>
              </Col>
              {userData?.user_type === 'company' && (
                <>
                  <Col xs={6}>
                    <Styled.DivStyle>
                      <SmallTitle>COMPANY</SmallTitle>
                      <BodyText>
                        {toSentenceCase(userData?.company_name)}
                      </BodyText>
                    </Styled.DivStyle>
                  </Col>
                  <Col xs={6}>
                    <Styled.DivStyle>
                      <SmallTitle>COMPANY USER TYPE</SmallTitle>
                      <BodyText>
                        {toSentenceCase(userData?.company_type)}
                      </BodyText>
                    </Styled.DivStyle>
                  </Col>
                  <Col xs={6}>
                    <Styled.DivStyle>
                      <SmallTitle>DEALER REGISTRATION NUMBER</SmallTitle>
                      <BodyText>{userData?.registration_number}</BodyText>
                    </Styled.DivStyle>
                  </Col>
                  <Col xs={6}>
                    <Styled.DivStyle>
                      <SmallTitle>REGISTERED ADDRESS</SmallTitle>
                      <BodyText>{userData?.address}</BodyText>
                    </Styled.DivStyle>
                  </Col>
                </>
              )}
              <Col xs={6}>
                <Styled.DivStyle>
                  <SmallTitle>USER POSITION</SmallTitle>
                  <BodyText>{toSentenceCase(userData?.position)}</BodyText>
                </Styled.DivStyle>
              </Col>
              <Col xs={6}>
                <Styled.DivStyle>
                  <SmallTitle>DATE ADDED</SmallTitle>
                  <BodyText>{userData?.created_at}</BodyText>
                </Styled.DivStyle>
              </Col>
              {userData?.user_type !== 'company' && <Col xs={6} />}
            </Row>
          </Col>
        </Card>
        <SubmitCard
          padding="0"
          primaryButtonTitle="Edit"
          primaryPermission={['user.update']}
          primaryButtonType="submit"
          primaryButtonDisabled={false}
          primaryButtonWidth="112px"
          secondaryButtonType="button"
          secondaryButtonDisabled={false}
          isLoading={deleteUserLoading}
          secondaryPermission={['user.delete']}
          secondaryButtonWidth="112px"
          submitClick={() => {
            navigate(`/app/user-management/users/edit/${id}`);
          }}
          submitClickSecondary={() => {
            launchDeleteConfirmation();
          }}
          secondaryButtonTitle="Delete"
        />
      </Row>
    );
  }
  return (
    <Styled.Div>
      <Loader loading />
    </Styled.Div>
  );
};

UserView.defaultProps = {};

UserView.propTypes = {};

export default UserView;
